import React, { useState } from 'react';
import { User, Plus, RefreshCw } from 'lucide-react';
import { usePageNavigation } from '../contexts/NavigationContext';
import SearchInput from '../components/ui/SearchInput';
import ClientCard from '../components/ui/ClientCard';
import ClientForm from '../components/ui/ClientForm';
import { ClientCardSkeleton } from '../components/ui/LoadingSpinner';
import { useClients } from '../hooks/useClients';
import type { Client } from '../types';

const Clients: React.FC = () => {
  const { 
    isLoading, 
    isInitialized,
    error, 
    createClient, 
    updateClient, 
    deleteClient, 
    filterClients,
    clearError,
    refreshClients
  } = useClients();
  const { setPageNavigation } = usePageNavigation();

  // Set up page navigation
  React.useEffect(() => {
    setPageNavigation('Clients', [
      { label: 'Dashboard', href: '/' },
      { label: 'Clients', href: '/clients' }
    ]);
  }, [setPageNavigation]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | undefined>(undefined);

  const filteredClients = filterClients(searchTerm);

  const handleAddClient = () => {
    setEditingClient(undefined);
    setShowAddForm(true);
    clearError();
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setShowAddForm(true);
    clearError();
  };

  const handleDeleteClient = async (id: string) => {
    try {
      await deleteClient(id);
    } catch (error) {
      // Error is handled by the hook
      console.error('Delete failed:', error);
    }
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
      } else {
        await createClient(formData);
      }
      setShowAddForm(false);
      setEditingClient(undefined);
    } catch (error) {
      // Error is handled by the hook, form stays open
    }
  };

  const handleFormCancel = () => {
    setShowAddForm(false);
    setEditingClient(undefined);
    clearError();
  };

  const handleRefresh = () => {
    refreshClients();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Client Management</h1>
          <p className="text-gray-400 mt-1">Manage and track all your clients</p>
        </div>
        
        <button
          onClick={handleAddClient}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>New Client</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
          <button 
            onClick={clearError}
            className="mt-2 text-sm text-red-300 hover:text-red-100 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center space-x-4">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search clients..."
            className="flex-1"
          />
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-3 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
            title="Refresh clients"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Add/Edit Client Form */}
      {showAddForm && (
        <ClientForm
          client={editingClient}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={isLoading}
        />
      )}

      {/* Client List */}
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">
            Clients ({filteredClients.length})
          </h2>
        </div>
        
        {!isInitialized || (isLoading && !showAddForm) ? (
          <div className="divide-y divide-gray-700">
            {[...Array(3)].map((_, index) => (
              <ClientCardSkeleton key={index} />
            ))}
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">
              {searchTerm ? 'No clients found matching your search.' : 'No clients found. Add your first client to get started.'}
            </p>
            {error && (
              <button
                onClick={handleRefresh}
                className="mt-4 text-blue-400 hover:text-blue-300 underline"
              >
                Try refreshing the data
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {filteredClients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onEdit={handleEditClient}
                onDelete={handleDeleteClient}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;
